import { useCallback } from "react";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import {
  PLATFORM_TOUR_PATHS,
  PLATFORM_TOUR_PREFERENCE_KEY,
  PLATFORM_TOUR_STATUS,
  PLATFORM_TOUR_WIDGET_DAYS,
} from "constants/productTour";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import {
  useGetUserProfileQuery,
  useUpdateUserPreferenceMutation,
} from "store/apis/profile";
import { PlatformTourPreference } from "types/Models/user";

type UsePlatformTourResult = {
  shouldShowModal: boolean;
  shouldShowWidget: boolean;
  isProductTourActive: boolean;
  updateTour: (value: Partial<PlatformTourPreference>) => Promise<void>;
};

const WIDGET_STATUSES: string[] = [
  PLATFORM_TOUR_STATUS.SKIPPED,
  PLATFORM_TOUR_STATUS.IN_PROGRESS,
];

export const usePlatformTour = (): UsePlatformTourResult => {
  const { pathname } = useLocation();
  const { isCustomer } = useRoleBasedView();
  const { has_paid_for_tax_filing } = useCurrentGroupContext();
  const { data: profile } = useGetUserProfileQuery();
  const [updateUserPreference] = useUpdateUserPreferenceMutation();

  const tour = profile?.preferences?.[PLATFORM_TOUR_PREFERENCE_KEY];
  const isOnTourPage = PLATFORM_TOUR_PATHS.includes(pathname);
  const isProductTourActive = Boolean(
    isCustomer &&
      tour &&
      !has_paid_for_tax_filing &&
      dayjs().isBefore(
        dayjs(tour.created_at).add(PLATFORM_TOUR_WIDGET_DAYS, "day")
      )
  );

  const shouldShowWidget = Boolean(
    isProductTourActive &&
      isOnTourPage &&
      tour &&
      WIDGET_STATUSES.includes(tour.status) &&
      !tour.is_widget_dismissed
  );

  const shouldShowModal =
    isProductTourActive &&
    isOnTourPage &&
    tour?.status === PLATFORM_TOUR_STATUS.NOT_STARTED;

  const updateTour = useCallback(
    async (value: Partial<PlatformTourPreference>) => {
      try {
        await updateUserPreference({
          key: PLATFORM_TOUR_PREFERENCE_KEY,
          value,
        }).unwrap();
      } catch (error) {
        console.error("Failed to update platform tour", error);
      }
    },
    [updateUserPreference]
  );

  return {
    shouldShowModal,
    shouldShowWidget,
    isProductTourActive,
    updateTour,
  };
};
